import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EnumActionsPermissions } from '@models/EnumPermissions';
import { AICurrencyCode, EnumAICurrency, IAIConfigurationModel, IAIConfigurationSubmitModel, IAIConsumptionMonthModel } from '@models/AI';
import { IDashboardData } from '@models/Dashboard';

@Component({
    selector: 'configurations-ai',
    templateUrl: './ai.component.html',
    styleUrls: ['./ai.component.scss'],
})
export class AIConfigurationsComponent implements OnInit, OnChanges {

    private static readonly MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

    @Input('data') Data: IAIConfigurationModel;

    /** histórico mensal do ano selecionado */
    @Input('consumption') Consumption: IAIConsumptionMonthModel[] = [];

    @Input('years') Years: number[] = [];

    @Input('year') Year: number;

    /** a página trata do alerta/loading/toast e devolve se guardou */
    @Input('save') save: (data: IAIConfigurationSubmitModel) => Promise<boolean>;

    /** feedback de validação, entregue pela página */
    @Input('showToast') showToast: (message: string) => Promise<any>;

    @Output('yearChange') YearChange: EventEmitter<number> = new EventEmitter<number>();

    public Form: FormGroup;

    public IsEditing: boolean = false;

    public Chart: IDashboardData;

    public EnumActionsPermissions = EnumActionsPermissions;
    public EnumAICurrency = EnumAICurrency;

    constructor() {

        this.onCreateFormElements();
    }

    ngOnInit() {

        this.onCreateFormElements();
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes['Data'] && !this.IsEditing)
            this.onCreateFormElements();

        if (changes['Consumption'])
            this.onCreateChart();
    }

    /**
     * a chave nunca vem do backend: fica vazia e só é enviada se for escrita
     */
    private onCreateFormElements(data: IAIConfigurationModel = this.Data) {

        this.Form = new FormGroup({

            IsActive: new FormControl(data?.IsActive ?? false, []),
            ApiKey: new FormControl('', []),
            IdCurrency: new FormControl(data?.IdCurrency || EnumAICurrency.EUR, [Validators.required]),
            CostLimitMonth: new FormControl(data?.CostLimitMonth ?? null, [Validators.min(0)]),
            Model: new FormControl(data?.Model || null, [])
        });

        this.Form.disable();
    }

    /**
     * custo por mês do ano selecionado
     */
    private onCreateChart() {

        const months = AIConfigurationsComponent.MONTHS.map((label, index) => {

            const month = this.Consumption?.find(it => +it?.Month == (index + 1));

            return { Label: label, Cost: month?.Cost || 0 };
        });

        this.Chart = {
            IdType: 2,
            Title: '',
            labels: months.map(it => it.Label),
            datasets: [{ label: 'Custo', data: months.map(it => it.Cost) }]
        };
    }

    public get Currency(): EnumAICurrency {

        return this.Form?.get('IdCurrency')?.value || this.Data?.IdCurrency || EnumAICurrency.EUR;
    }

    /** limite configurado; 0/null significa sem limite */
    public get CostLimit(): number {

        return +(this.Data?.CostLimitMonth || 0);
    }

    public get CurrentMonthCost(): number {

        return +(this.Data?.CurrentMonthCost || 0);
    }

    /** percentagem do limite já consumida no mês corrente */
    public get CostLimitUsedPercent(): number {

        if (this.CostLimit <= 0) return 0;

        return Math.min(Math.round((this.CurrentMonthCost / this.CostLimit) * 100), 100);
    }

    /** cor do indicador: verde até 75%, amarelo até 100%, vermelho no limite */
    public get CostLimitVariant(): 'pos' | 'warn' | 'neg' | '' {

        if (this.CostLimit <= 0) return '';

        const percent = this.CostLimitUsedPercent;

        return percent >= 100 ? 'neg' : (percent >= 75 ? 'warn' : 'pos');
    }

    /** nome legível do modelo: "claude-sonnet-5" -> "Claude Sonnet 5 (equilibrado)" */
    public ModelLabel(model: string): string {

        if (!model) return '';

        const name = model.replace(/^claude-/, '').split('-')
            .map((part, index) => index === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part)
            .join(' ')
            .replace(/(\d) (\d)/, '$1.$2');

        const profile = model.includes('opus') ? 'mais capaz'
            : model.includes('sonnet') ? 'equilibrado'
            : model.includes('haiku') ? 'mais económico' : '';

        return `Claude ${name}` + (profile ? ` (${profile})` : '');
    }

    /** preço do modelo escolhido no formulário, para quem escolhe saber o que custa */
    public get SelectedModelPrice(): string {

        const model = this.Data?.Models?.find(it => it.Model === (this.Form?.get('Model')?.value || this.Data?.Model));

        if (!model) return '';

        const usd = (value: number) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'USD' }).format(+value || 0);

        return `${usd(model.PriceInputMillionUSD)} por milhão de tokens de entrada e ${usd(model.PriceOutputMillionUSD)} de saída.`;
    }

    /** valor do interruptor no formulário (muda antes de guardar) */
    public get IsActiveValue(): boolean {

        return !!this.Form?.get('IsActive')?.value;
    }

    /** "outubro de 2026" */
    public get CurrentMonthLabel(): string {

        return new Intl.DateTimeFormat('pt-PT', { month: 'long', year: 'numeric' }).format(new Date());
    }

    /** dia em que o contador mensal recomeça: "1 de novembro" */
    public get ResetDateLabel(): string {

        const now = new Date();

        return new Intl.DateTimeFormat('pt-PT', { day: 'numeric', month: 'long' })
            .format(new Date(now.getFullYear(), now.getMonth() + 1, 1));
    }

    /**
     *
     * @param value
     * @param idCurrency
     * @returns
     */
    public FormatCost(value: number, idCurrency: EnumAICurrency = this.Currency): string {

        return new Intl.NumberFormat('pt-PT', {
            style: 'currency',
            currency: AICurrencyCode[idCurrency] || AICurrencyCode[EnumAICurrency.EUR],
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(+(value || 0));
    }

    /**
     *
     * @param item
     * @returns
     */
    public MonthLabel(item: IAIConsumptionMonthModel): string {

        return `${AIConfigurationsComponent.MONTHS[(+item?.Month || 1) - 1]} ${item?.Year || ''}`;
    }

    public OnEdit(ev?: any) {

        this.IsEditing = true;

        this.Form.enable();
    }

    /**
     *
     * @param ev
     */
    public OnCancelEdit(ev?: any) {

        this.IsEditing = false;

        this.onCreateFormElements();
    }

    /**
     * validação: com AI ativa é obrigatório existir chave e limite definido
     * @returns
     */
    public async OnSave(ev?: any) {

        this.Form.markAllAsTouched();

        const isActive = this.Form.get('IsActive').value ?? false;
        const apiKey = (this.Form.get('ApiKey').value || '').trim();
        const costLimit = +(this.Form.get('CostLimitMonth').value || 0);

        if (this.Form.invalid)
            return await this.showToast?.('Preencha todos os campos assinalados.');

        if (isActive && !apiKey && !this.Data?.HasApiKey)
            return await this.showToast?.('Indique a chave de API para ativar a AI.');

        if (isActive && costLimit <= 0)
            return await this.showToast?.('Defina um limite de custo mensal maior que zero.');

        const submit: IAIConfigurationSubmitModel = {
            IsActive: isActive,
            ApiKey: apiKey,
            IdCurrency: +this.Form.get('IdCurrency').value,
            CostLimitMonth: costLimit,
            Model: this.Form.get('Model').value || null
        };

        const saved = this.save ? await this.save(submit) : false;

        if (!saved) return;

        this.IsEditing = false;

        this.onCreateFormElements();
    }

    /**
     *
     * @param ev
     */
    public OnChangeYear(ev: any) {

        const year = +(ev?.detail?.value) || this.Year;

        if (year == this.Year) return;

        this.Year = year;

        this.YearChange.emit(year);
    }
}
